/**
 * The force balance for one tick: lift, drag, thrust, gravity (AD-007c).
 *
 * There is deliberately **no sideforce** in that list any more (AD-007i). The
 * span-axis term that used to restore sideslip was translational, so it curved
 * the flight path without bleeding any speed — the last covert path-curving
 * pathway after AD-007h took the horizontal component off lift, and the whole
 * of the flown 5-second pull's residual overshoot. Slip now costs drag
 * (`aero.ts`'s `slipDragPerRad2·β²`, inside `dragForceN`, path-aligned) and
 * rotates the attitude (`controls.ts`'s `slipRelaxationRates`, applied
 * kinematically in `step.ts`), which is what AD-007c's own "slip angle bleeds
 * speed and yaws the nose" always described.
 *
 * Two non-obvious terms sit here rather than in aero.ts because they
 * are gameplay rules rather than aerodynamics:
 *
 * - **The produced turn rate (AD-007h).** Heading rate is *produced* from bank
 *   angle and elevator pull, not derived from lift geometry — the calibration
 *   session found the original turning at 2.5–2.7× what integrating lift-vector
 *   geometry through the measured control rates gives. Replacing that pathway
 *   means the lift force's own horizontal, path-curving component (its
 *   projection onto `worldLateral`, the world-horizontal axis perpendicular to
 *   the flight path) is *not* applied as a force any more — `verticalPlaneLiftDir`
 *   strips it out, leaving the vertical/pitch-plane component (climb, dive,
 *   load factor) exactly as it was, since `worldLateral` is horizontal by
 *   construction and never carries a y-component to lose. `controls.ts`'s
 *   `producedHeadingRateRadPerSec` computes the replacement from `.DAT` offsets
 *   116/118 (via `turnRateLimit`, corrected by the measured `turnRateCapGain`);
 *   this module's job is the energy side of it — a produced turn still costs
 *   induced drag scaled by the commanded rate — and reporting the rate itself
 *   in `ForceOutput` for `step.ts` to apply as a kinematic world-frame yaw of
 *   the velocity vector *and* the orientation (so the nose carries around with
 *   the turn rather than opening a sideslip against it — see `math3d.ts`'s
 *   `yawAboutWorldUp`/`applyWorldYaw`). Because none of this touches `liftN`
 *   the scalar, `loadFactor` is a clean reading of how much lift the wing
 *   (AoA) is actually generating, independent of how fast the aircraft is
 *   turning — which is what `spin.ts`'s coordinated-turn check wants: a bank
 *   held without enough back pressure to hold `1/cos φ` g genuinely is
 *   uncoordinated now, not an artifact of a turn-rate cap eating into the lift
 *   the pilot asked for.
 * - **The Novice lift assist.** The manual promises "your aircraft will not
 *   nose down in a turn"; the assist tops the lift up to hold the vertical
 *   component against weight. Deliberately unphysical — that is the tier. (Its
 *   turn-side counterpart, auto-coordination, lives in `producedHeadingRateRadPerSec`.)
 * - **Thrust acts along the flight path, not along the nose.** A fuselage-mounted
 *   propeller physically pushes along the nose, and this module used to model it
 *   that way; the original does not behave like that, and the difference is
 *   large enough to see. Applied along the nose, the component of thrust
 *   *perpendicular* to the flight path is a near-constant force that deflects
 *   the velocity vector by roughly `Δv/|v|` per tick — so its angular effect
 *   grows without bound as a manoeuvre bleeds speed. That is exactly the regime
 *   the original was measured in and does *not* show: a full-back zoom bottoms
 *   out near 20 mph with the nose merely sagging (re-sessions/calib/NOTES.md
 *   §4.5), and the 5-second banked pull swings 173° of heading, not the ~230°
 *   nose-thrust produces here at the same commanded rate
 *   (flight-tunables.md §8.1). Along-the-path thrust reproduces both, and it is
 *   also the natural shape of a 1990 point-mass integrator, which carries speed
 *   as a scalar along the flight path and turns the path with a separate
 *   kinematic term — the same structure AD-007h found in the turn mechanism.
 *   `thrustAlongPathFraction` names the choice as a tunable rather than baking
 *   it in; see `thrustDir`.
 */

import { airDataFrom, dragForceN, liftCoefficient, type AirData } from "./aero.ts";
import type { AirProperties } from "./atmosphere.ts";
import {
  alphaReferenceRad,
  commandedLoadFactor,
  producedPathPitchRateRadPerSec,
} from "./elevator-path.ts";
import { producedHeadingRateRadPerSec } from "./produced-turn.ts";
import type { AircraftSpec } from "./aircraft-spec.ts";
import {
  BODY_FORWARD,
  BODY_RIGHT,
  BODY_UP,
  addVec3,
  bankAngle,
  crossVec3,
  dotVec3,
  inverseRotateVec3,
  normalizeVec3,
  rotateVec3,
  scaleVec3,
  type Quat,
  type Vec3,
} from "./math3d.ts";
import type { Tunables } from "./tunables.ts";
import { GRAVITY_MPS2, clamp } from "./units.ts";

const WORLD_UP: Vec3 = { x: 0, y: 1, z: 0 };

export interface ForceInput {
  readonly spec: AircraftSpec;
  readonly orientation: Quat;
  readonly velocity: Vec3;
  readonly air: AirProperties;
  readonly thrustN: number;
  /** Lift multiplier from structural damage (1 = intact). */
  readonly liftFactor: number;
  /** Novice bank-to-turn assist, from the subsystem table. */
  readonly liftAssist: boolean;
  /** Elevator position, so the assist can stand aside when the pilot pushes. */
  readonly pitchInput: number;
  /** 0..1 control-surface authority left after damage, so a shot-away elevator commands less path curvature too (AD-007n). */
  readonly controlAuthority: number;
  readonly tunables: Tunables;
}

export interface ForceOutput {
  readonly acceleration: Vec3;
  readonly airData: AirData;
  readonly liftN: number;
  readonly loadFactor: number;
  /** True when the directional turn-performance ceiling is what limited the produced turn. */
  readonly turnLimited: boolean;
  /** Produced heading rate this tick, rad/s, right-positive (AD-007h) — `step.ts` applies this kinematically after integrating the body-rate orientation and the force-based velocity. */
  readonly headingRateRadPerSec: number;
  /**
   * Produced flight-path pitch rate this tick, rad/s, nose-up-positive
   * (AD-007n change 3, `elevator-path.ts`). The vertical-plane counterpart of
   * `headingRateRadPerSec`: `step.ts` rotates the velocity **and** the
   * orientation rigidly about the path's world-horizontal axis by this, so the
   * nose goes round with the path and the angle of attack is carried through
   * unchanged. Exactly zero with the stick centred.
   */
  readonly pathPitchRateRadPerSec: number;
  /**
   * The α the elevator's load-factor command implies (AD-007n change 2,
   * `elevator-path.ts`'s `alphaReferenceRad`) — what `controls.ts`'s
   * α-relaxation relaxes toward. Equal to the current α with the stick centred.
   */
  readonly referenceAlphaRad: number;
}

interface FlightAxes {
  readonly forward: Vec3;
  readonly liftDir: Vec3;
  /** World-horizontal, perpendicular to the flight path — the axis the produced turn now owns (see `verticalPlaneLiftDir`). */
  readonly worldLateral: Vec3;
}

function flightAxes(input: ForceInput, speed: number): FlightAxes {
  const bodyUp = rotateVec3(input.orientation, BODY_UP);
  const bodyRight = rotateVec3(input.orientation, BODY_RIGHT);
  const forward = rotateVec3(input.orientation, BODY_FORWARD);
  const flightPath = speed > 1e-6 ? normalizeVec3(input.velocity, forward) : forward;
  // The span axis is still how the lift direction is built (perpendicular to
  // both the flight path and the wing plane), but nothing pushes along it any
  // more — see the module doc — so it stays local.
  const spanAxis = normalizeVec3(crossVec3(flightPath, bodyUp), bodyRight);
  const liftDir = normalizeVec3(crossVec3(spanAxis, flightPath), bodyUp);
  const worldLateral = normalizeVec3(crossVec3(flightPath, WORLD_UP), bodyRight);
  return { forward, liftDir, worldLateral };
}

/**
 * `axes.liftDir` with its `worldLateral` (world-horizontal, path-curving)
 * component removed — see the module doc. `worldLateral` is horizontal by
 * construction, so this is exactly `liftDir` when the aircraft is not banked
 * (straight climbs, dives and pull-outs are untouched) and falls away toward
 * zero as bank approaches a knife edge, the same `cos φ` loss coordinated-turn
 * geometry always had for the *vertical* component — only now the horizontal
 * component that used to curve the path is dropped instead of applied.
 */
function verticalPlaneLiftDir(axes: FlightAxes): Vec3 {
  const lateral = dotVec3(axes.liftDir, axes.worldLateral);
  return addVec3(axes.liftDir, scaleVec3(axes.worldLateral, -lateral));
}

/**
 * Where thrust pushes: along the flight path, not along the nose (AD-007c
 * addendum, see the module doc). `thrustAlongPathFraction` interpolates between
 * the two and is **1** — pure flight-path thrust — so this normally returns
 * `pathDir` unchanged; the blend exists because the tunable is a measured
 * quantity that a later session could move off its endpoint, not because any
 * evidence today asks for a partial blend. The interpolated direction is
 * re-normalized, so an intermediate fraction is a genuine direction rather than
 * a shortened vector (thrust *magnitude* is the powerplant's business).
 */
function thrustDir(axes: FlightAxes, pathDir: Vec3, tunables: Tunables): Vec3 {
  const alongPath = clamp(tunables.thrustAlongPathFraction, 0, 1);
  if (alongPath >= 1) return pathDir;
  if (alongPath <= 0) return axes.forward;
  return normalizeVec3(
    addVec3(scaleVec3(pathDir, alongPath), scaleVec3(axes.forward, 1 - alongPath)),
    axes.forward,
  );
}

/**
 * Tops lift up to whatever holds the vertical component against weight — the
 * manual's "your aircraft will not nose down in a turn". It only ever adds
 * lift, and only while the pilot is not pushing, so a deliberate descent still
 * descends; what it removes is the cosine loss of banking.
 */
function assistedLiftN(input: ForceInput, axes: FlightAxes, liftN: number): number {
  const { tunables } = input;
  if (!input.liftAssist || axes.liftDir.y <= 0.1 || input.pitchInput < 0) return liftN;
  const weightN = input.spec.massKg * GRAVITY_MPS2;
  const target = Math.min(weightN / axes.liftDir.y, weightN * tunables.noviceMaxLiftAssistFactor);
  if (target <= liftN) return liftN;
  return liftN + (target - liftN) * tunables.noviceLiftAssistFraction;
}

/**
 * The square of the lift coefficient the elevator's load-factor command implies,
 * capped at the wing's own `clMax` so a near-stationary aeroplane cannot conjure
 * an unbounded drag out of a vanishing dynamic pressure — the `1/q` trap
 * flight-tunables.md §8.2 found in nose-aligned thrust, in its drag clothing.
 */
function commandedClSquared(spec: AircraftSpec, liftPerClN: number, commandedN: number): number {
  if (liftPerClN <= 1e-6) return 0;
  const cl = Math.min(Math.abs((commandedN * spec.massKg * GRAVITY_MPS2) / liftPerClN), spec.aero.clMax);
  return cl * cl;
}

/**
 * AD-007n's whole contribution to a tick, computed here because this is where
 * the load factor the wing is *actually* delivering is known.
 *
 * Every quantity it produces is identically zero-effect with the stick centred —
 * the produced rate is zero, the reference α is the current α and the pull drag
 * is nil — which is what leaves every hands-off rail bit-identical to AD-007l/m.
 *
 * The drag term is AD-007h's own rule for the produced turn ("a produced turn
 * still pays induced-drag energy cost") applied to the vertical plane: a
 * produced pull pays for the lift it is *asking* for, not only for the lift the
 * wing is making. That is where a max-rate manoeuvre's price comes from — the
 * original's full-back traces bleed 40 mph in 2.8 s wings level and 43 mph in
 * 5.3 s in a banked pull, far more than a wing held at the angle of attack those
 * traces show could ever cost.
 */
function elevatorPathway(
  input: ForceInput,
  airData: AirData,
  liftCl: number,
  loadFactor: number,
): { readonly pathPitchRateRadPerSec: number; readonly referenceAlphaRad: number; readonly pullDragN: number } {
  const { spec } = input;
  const liftPerClN = airData.dynamicPressurePa * spec.aero.wingAreaM2 * input.liftFactor;
  const demand = {
    aero: spec.aero,
    tunables: input.tunables,
    bankRad: bankAngle(input.orientation),
    pitchInput: input.pitchInput,
    loadFactor,
    airspeedMps: airData.airspeedMps,
    alphaRad: airData.alphaRad,
    pitchRateMaxRadPerSec: spec.control.pitchRateMaxRadPerSec,
    controlAuthority: input.controlAuthority,
  };
  const commandedN = commandedLoadFactor(demand);
  return {
    pathPitchRateRadPerSec: producedPathPitchRateRadPerSec(demand),
    referenceAlphaRad: alphaReferenceRad({
      tunables: input.tunables,
      alphaStallRad: spec.aero.alphaStallRad,
      pitchInput: input.pitchInput,
      alphaRad: airData.alphaRad,
    }),
    pullDragN:
      input.tunables.elevatorPullDragGain *
      Math.max(0, commandedClSquared(spec, liftPerClN, commandedN) - liftCl * liftCl) *
      airData.dynamicPressurePa *
      spec.aero.wingAreaM2,
  };
}

export function computeForces(input: ForceInput): ForceOutput {
  const { spec } = input;
  const speed = Math.hypot(input.velocity.x, input.velocity.y, input.velocity.z);
  const airData = airDataFrom(
    inverseRotateVec3(input.orientation, input.velocity),
    input.air.densityKgM3,
    input.tunables.wingIncidenceRad,
  );
  const axes = flightAxes(input, speed);

  const liftCl = liftCoefficient(spec.aero, airData.alphaRad);
  const rawLiftN = airData.dynamicPressurePa * spec.aero.wingAreaM2 * liftCl * input.liftFactor;
  const liftN = assistedLiftN(input, axes, rawLiftN);

  const turn = producedHeadingRateRadPerSec({
    turn: spec.turn,
    aero: spec.aero,
    tunables: input.tunables,
    bankRad: bankAngle(input.orientation),
    pitchInput: input.pitchInput,
    alphaRad: airData.alphaRad,
    bankToTurnAssist: input.liftAssist,
  });
  const turnDragN =
    input.tunables.turnInducedDragGain *
    turn.rateRadPerSec *
    turn.rateRadPerSec *
    airData.dynamicPressurePa *
    spec.aero.wingAreaM2;

  const loadFactor = liftN / (spec.massKg * GRAVITY_MPS2);
  const elevator = elevatorPathway(input, airData, liftCl, loadFactor);
  const dragN = dragForceN(spec.aero, airData, liftCl) + turnDragN + elevator.pullDragN;
  const pathDir = normalizeVec3(input.velocity, axes.forward);
  const totalN = addVec3(
    addVec3(
      scaleVec3(verticalPlaneLiftDir(axes), liftN),
      scaleVec3(pathDir, -dragN),
    ),
    addVec3(scaleVec3(thrustDir(axes, pathDir, input.tunables), input.thrustN), {
      x: 0,
      y: -spec.massKg * GRAVITY_MPS2,
      z: 0,
    }),
  );

  return {
    acceleration: scaleVec3(totalN, 1 / spec.massKg),
    airData,
    liftN,
    loadFactor,
    turnLimited: turn.atCeiling,
    headingRateRadPerSec: turn.rateRadPerSec,
    pathPitchRateRadPerSec: elevator.pathPitchRateRadPerSec,
    referenceAlphaRad: elevator.referenceAlphaRad,
  };
}

